import {useEffect, useMemo, useState} from 'react';
import {useQuery, useQueryClient} from 'react-query';
import type {Edge, Node} from 'reactflow';
import {AutomationService} from 'crqlar-react-common/dist/services/automation/AutomationService';
import {fromFlowEdges, fromFlowNodes} from '../utils/flowGraph';
import {canSimulate} from './flowPreviewModel';

export const FLOW_PREVIEW_QUERY_KEY = 'automation-flow-preview';

const SETTLE_MS = 300;

/** A refresh shorter than this leaves the numbers as they are; a longer one dims them. */
const SLOW_MS = 400;

/** A short, stable stand-in for the canvas in the query key (FNV-1a), so a big email body is not the key. */
const hashOf = (text: string): string => {
    let hash = 0x811c9dc5;
    for (let index = 0; index < text.length; index++) {
        hash ^= text.charCodeAt(index);
        hash = Math.imul(hash, 0x01000193);
    }
    return (hash >>> 0).toString(36) + text.length.toString(36);
};

/**
 * What the canvas as it is (saved or not) would do if it ran now. Queries only while `on`, debounced after the last
 * change; moving cards asks nothing (positions are not in the key). The previous answer stays while a newer one loads.
 * Leaving preview drops its answers, so entering again always asks the API anew.
 * A superseded request is not cancelled: `AutomationService.previewFlow` takes no abort signal.
 */
export const useFlowPreview = (flowId: string | undefined, nodes: Node[], edges: Edge[], on: boolean) => {
    // Needed while preview is off too: the header shows the Preview button.
    const previewable = useMemo(() => canSimulate(nodes.map(node => ({type: node.data.type}))), [nodes]);

    const [settled, setSettled] = useState<{nodes: Node[]; edges: Edge[]} | null>(on ? {nodes, edges} : null);

    // Set while rendering, so the first render in preview already asks about the canvas as it is now.
    if (on !== !!settled) setSettled(on ? {nodes, edges} : null);

    useEffect(() => {
        if (!settled || (settled.nodes === nodes && settled.edges === edges)) return;
        // The canvas arriving is not an edit: the first question goes out at once.
        if (settled.nodes.length === 0) {
            setSettled({nodes, edges});
            return;
        }
        const timer = window.setTimeout(() => setSettled({nodes, edges}), SETTLE_MS);
        return () => window.clearTimeout(timer);
    }, [nodes, edges, settled]);

    const canvas = useMemo(() => settled && {
        nodes: fromFlowNodes(settled.nodes),
        connections: fromFlowEdges(settled.edges),
    }, [settled]);

    const key = useMemo(() => canvas && hashOf(JSON.stringify({
        nodes: canvas.nodes.map(({position, ...node}) => node),
        connections: canvas.connections,
    })), [canvas]);

    const asking = on && !!canvas && canSimulate(canvas.nodes);

    // Every preview session asks afresh: guests, runs and contact history change between sessions.
    const queryClient = useQueryClient();
    useEffect(() => {
        if (!on) queryClient.removeQueries([FLOW_PREVIEW_QUERY_KEY, flowId]);
    }, [on, flowId, queryClient]);

    const query = useQuery({
        queryKey: [FLOW_PREVIEW_QUERY_KEY, flowId, key],
        queryFn: () => AutomationService.previewFlow(flowId!, canvas!),
        enabled: !!flowId && asking,
        keepPreviousData: true,
        refetchOnWindowFocus: false,
        staleTime: 60 * 1000,
        cacheTime: 30 * 60 * 1000,
        retry: false,
    });

    const [slow, setSlow] = useState(false);
    useEffect(() => {
        if (!asking || !query.isFetching) {
            setSlow(false);
            return;
        }
        const timer = window.setTimeout(() => setSlow(true), SLOW_MS);
        return () => window.clearTimeout(timer);
    }, [asking, query.isFetching]);

    return {
        previewable,
        preview: asking ? query.data : undefined,
        loading: asking && !query.data && !query.isError,
        slow,
        failed: asking && query.isError,
        retry: () => {
            query.refetch();
        },
    };
};
